import { useActiveOrganizationId } from "@/contexts"
import { ApiQueryName } from "@/models/api"
import { useQueryKey, useRenameQueryResult } from "@/services/utils"
import { httpClient, toHttpError } from "@/utils/api"
import { useQuery } from "@tanstack/react-query"

import type { DisaggregationLeaf } from "../pages/variance-route/disaggregation.types"

// The variance leaf (`/neat/insights/variance/{site_id}/{cycle_id}`) returns
// `Any` in insights-api's OpenAPI, so it's absent from the generated typed
// `client()` schema — use the shared axios `httpClient` (same as
// use-get-bill-document). The response interceptor camelCases the body, so the
// hook's contract is the camelCase `DisaggregationLeaf`. `enduse` values are
// data, not keys, so they pass through untransformed (interior_lighting stays).
const getDisaggregation = async (
  organizationId: number,
  siteId: string,
  cycleId: string
): Promise<DisaggregationLeaf> => {
  try {
    const res = await httpClient.get<DisaggregationLeaf>(
      `/api/v1/neat/insights/variance/${siteId}/${cycleId}`,
      { params: { organization_id: organizationId } }
    )
    return res.data
  } catch (error: unknown) {
    throw toHttpError(error)
  }
}

/**
 * Lazily resolves an anomalous month's PRISM end-use disaggregation. No fetch
 * until the drawer is open (siteId + cycleId both set) — same discipline as
 * useGetBillDocument.
 */
export const useGetDisaggregation = (
  siteId: string | null,
  cycleId: string | null
) => {
  const { activeOrganizationId } = useActiveOrganizationId()
  const queryKey = useQueryKey(
    ApiQueryName.Disaggregation,
    "getOne",
    siteId != null && cycleId != null ? `${siteId}:${cycleId}` : null,
    {}
  )
  const queryResult = useQuery<DisaggregationLeaf>({
    queryKey,
    enabled: siteId != null && cycleId != null && activeOrganizationId != null,
    queryFn: () =>
      getDisaggregation(
        Number(activeOrganizationId),
        siteId as string,
        cycleId as string
      ),
  })
  return useRenameQueryResult(queryResult, ApiQueryName.Disaggregation)
}
